'use client';

import { useEffect, useRef, type RefObject } from 'react';

import {
  HERO_SCENE_INTRO_ID,
  HERO_SWIRL_SCENE_DONE_ATTR,
} from '@/constants/heroScene';
import {
  NAVBAR_DATA_HIDDEN,
  NAVBAR_DATA_SCROLLED,
  NAVBAR_HERO_SENTINEL_ID,
  NAVBAR_MOBILE_SCROLL_MEDIA_QUERY,
  NAVBAR_SCROLL_DELTA_PX,
  NAVBAR_TOP_THRESHOLD_PX,
  SITE_MOBILE_NAVBAR_OFFSET_VALUE,
  SITE_MOBILE_NAVBAR_OFFSET_VAR,
} from '@/constants/navbar';
import { getLenisInstance } from '@/lib/scroll';

import type Lenis from 'lenis';

type UseNavbarScrollStateOptions = {
  headerRef: RefObject<HTMLElement | null>;
  /** When false (e.g. solid secondary pages), skip scroll chrome entirely. */
  enabled: boolean;
};

function clearNavbarScrollAttrs(header: HTMLElement): void {
  header.removeAttribute(NAVBAR_DATA_SCROLLED);
  header.removeAttribute(NAVBAR_DATA_HIDDEN);
}

function clearStickyNavOffset(): void {
  document.documentElement.style.removeProperty(SITE_MOBILE_NAVBAR_OFFSET_VAR);
}

function readSwirlSceneDone(): boolean {
  const intro = document.getElementById(HERO_SCENE_INTRO_ID);
  return intro?.hasAttribute(HERO_SWIRL_SCENE_DONE_ATTR) ?? false;
}

/**
 * Mobile primary-nav chrome: fixed bar that hides on scroll-down (including
 * mid-hero / mid-swirl) and reappears on scroll-up. Opaque off-white surface
 * engages after the hero swirl scene finishes (`data-hero-swirl-done`), with
 * a `#hero` IntersectionObserver fallback when the swirl never runs
 * (reduced motion). Desktop (`lg+`) leaves the absolute hero overlay alone.
 */
export function useNavbarScrollState({
  headerRef,
  enabled,
}: UseNavbarScrollStateOptions): void {
  const pastHeroRef = useRef(false);
  const swirlDoneRef = useRef(false);
  const hiddenRef = useRef(false);
  const lastScrollYRef = useRef(0);
  const enabledRef = useRef(enabled);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const mediaQuery = window.matchMedia(NAVBAR_MOBILE_SCROLL_MEDIA_QUERY);
    let lenisUnsub: (() => void) | null = null;
    let boundLenis: Lenis | null = null;

    const isOpaque = () => swirlDoneRef.current || pastHeroRef.current;

    const syncStickyNavOffset = () => {
      const showOffset =
        enabledRef.current &&
        mediaQuery.matches &&
        isOpaque() &&
        !hiddenRef.current &&
        !header.hasAttribute('data-menu-open');

      if (showOffset) {
        document.documentElement.style.setProperty(
          SITE_MOBILE_NAVBAR_OFFSET_VAR,
          SITE_MOBILE_NAVBAR_OFFSET_VALUE,
        );
      } else {
        clearStickyNavOffset();
      }
    };

    const apply = () => {
      if (!enabledRef.current || !mediaQuery.matches) {
        pastHeroRef.current = false;
        swirlDoneRef.current = false;
        hiddenRef.current = false;
        clearNavbarScrollAttrs(header);
        clearStickyNavOffset();
        return;
      }

      const menuOpen = header.hasAttribute('data-menu-open');
      const opaque = isOpaque();

      if (opaque) {
        header.setAttribute(NAVBAR_DATA_SCROLLED, '');
      } else {
        header.removeAttribute(NAVBAR_DATA_SCROLLED);
      }

      if (menuOpen) {
        hiddenRef.current = false;
        header.removeAttribute(NAVBAR_DATA_HIDDEN);
      } else if (hiddenRef.current) {
        header.setAttribute(NAVBAR_DATA_HIDDEN, '');
      } else {
        header.removeAttribute(NAVBAR_DATA_HIDDEN);
      }

      syncStickyNavOffset();
    };

    const onScrollY = (scrollY: number, direction?: 1 | -1 | 0) => {
      if (!enabledRef.current || !mediaQuery.matches) return;

      const y = Math.max(0, scrollY);

      if (y <= NAVBAR_TOP_THRESHOLD_PX) {
        hiddenRef.current = false;
        lastScrollYRef.current = y;
        apply();
        return;
      }

      if (direction === 1) {
        hiddenRef.current = true;
        lastScrollYRef.current = y;
      } else if (direction === -1) {
        hiddenRef.current = false;
        lastScrollYRef.current = y;
      } else {
        const delta = y - lastScrollYRef.current;
        if (Math.abs(delta) >= NAVBAR_SCROLL_DELTA_PX) {
          if (delta > 0) {
            hiddenRef.current = true;
          } else if (delta < 0) {
            hiddenRef.current = false;
          }
          lastScrollYRef.current = y;
        }
      }

      apply();
    };

    const onWindowScroll = () => {
      onScrollY(window.scrollY);
    };

    const onLenisScroll = (lenis: Lenis) => {
      onScrollY(lenis.scroll, lenis.direction);
    };

    const bindLenis = (lenis: Lenis) => {
      if (boundLenis === lenis) return;
      lenisUnsub?.();
      boundLenis = lenis;
      lenisUnsub = lenis.on('scroll', onLenisScroll);
      window.removeEventListener('scroll', onWindowScroll);
    };

    // Reduced-motion / no-GSAP fallback: opaque once #hero leaves the viewport.
    const hero = document.getElementById(NAVBAR_HERO_SENTINEL_ID);
    let heroObserver: IntersectionObserver | null = null;

    if (hero) {
      heroObserver = new IntersectionObserver(
        ([entry]) => {
          pastHeroRef.current = !entry.isIntersecting;
          apply();
        },
        { threshold: 0 },
      );
      heroObserver.observe(hero);
    }

    // Primary opaque gate: GSAP sets data-hero-swirl-done when swirl travel ends.
    const intro = document.getElementById(HERO_SCENE_INTRO_ID);
    let swirlObserver: MutationObserver | null = null;

    swirlDoneRef.current = readSwirlSceneDone();

    if (intro) {
      swirlObserver = new MutationObserver(() => {
        swirlDoneRef.current = intro.hasAttribute(HERO_SWIRL_SCENE_DONE_ATTR);
        apply();
      });
      swirlObserver.observe(intro, {
        attributes: true,
        attributeFilter: [HERO_SWIRL_SCENE_DONE_ATTR],
      });
    }

    lastScrollYRef.current = window.scrollY;

    const onMediaChange = () => {
      lastScrollYRef.current = window.scrollY;
      apply();
    };

    mediaQuery.addEventListener('change', onMediaChange);

    const menuObserver = new MutationObserver(() => {
      apply();
    });
    menuObserver.observe(header, {
      attributes: true,
      attributeFilter: ['data-menu-open'],
    });

    const existing = getLenisInstance();
    if (existing) {
      bindLenis(existing);
    } else {
      window.addEventListener('scroll', onWindowScroll, { passive: true });
    }

    apply();

    let cancelled = false;
    let frames = 0;
    const pollLenis = () => {
      if (cancelled || frames > 120) return;
      frames += 1;
      const late = getLenisInstance();
      if (late) {
        bindLenis(late);
        return;
      }
      requestAnimationFrame(pollLenis);
    };
    requestAnimationFrame(pollLenis);

    return () => {
      cancelled = true;
      heroObserver?.disconnect();
      swirlObserver?.disconnect();
      menuObserver.disconnect();
      mediaQuery.removeEventListener('change', onMediaChange);
      window.removeEventListener('scroll', onWindowScroll);
      lenisUnsub?.();
      clearNavbarScrollAttrs(header);
      clearStickyNavOffset();
    };
  }, [headerRef, enabled]);
}
